/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress, Alert, Button, Typography, Snackbar } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import projectService from '../../utils/apiRequests/projectService';
import urls from '../../urls';
import {
  setActiveProject,
  setLoading,
  setError,
  addChapterToProject,
  addSceneToChapter,
} from '../../features/projects/projectsSlice';
import ProjectLayout from './components/ProjectLayout/ProjectLayout';
import AddChapterModal from '../../components/AddChapterModal/AddChapterModal';
import AddSceneModal from '../../components/AddSceneModal/AddSceneModal';
import ImportDocumentModal from '../../components/ImportDocumentModal';
import ExportProjectModal from '../../components/ExportProjectModal/ExportProjectModal';
import EditProjectModal from '../../components/EditProjectModal';
import AddCharacterModal from '../../features/characters/AddCharacterModal';
import ShareProjectModal from '../../components/ShareProjectModal/ShareProjectModal';
import { ProjectAccessProvider } from '../../contexts/ProjectAccessContext';
import useThemeStyles from '../../hooks/useThemeStyles';
import useProjectCommands from './useProjectCommands';
import './ProjectView.css';

const ProjectView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { colors } = useThemeStyles();

  const activeProject = useSelector((state) => state.projects.activeProject);
  const loading = useSelector((state) => state.projects.loading);
  const error = useSelector((state) => state.projects.error);
  // const user = useSelector((state) => state.user.userInfo);

  const [localLoading, setLocalLoading] = useState(true);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(280);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(280);
  const [addChapterModalOpen, setAddChapterModalOpen] = useState(false);
  const [addSceneModalOpen, setAddSceneModalOpen] = useState(false);
  const [importDocumentModalOpen, setImportDocumentModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [editProjectModalOpen, setEditProjectModalOpen] = useState(false);
  const [addCharacterModalOpen, setAddCharacterModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedChapterForScene, setSelectedChapterForScene] = useState(null);
  const [refreshScenesCallback, setRefreshScenesCallback] = useState(null);
  const [chapterSceneFunctions, setChapterSceneFunctions] = useState({});
  const [suppressSceneLoading, setSuppressSceneLoading] = useState(false);
  const [exportToast, setExportToast] = useState(null); // { severity, message }
  const suppressTimeoutRef = useRef(null);

  // Derive active section, notes view, corkboard ID, and timeline ID from URL
  const { activeCenterSection, notesView, corkboardId, timelineId, gameTool } = useMemo(() => {
    const path = location.pathname;

    // Game design routes: /gamedesign or /gamedesign/:tool
    const gameDesignMatch = path.match(/\/gamedesign(?:\/([^/]+))?\/?$/);
    if (gameDesignMatch) {
      return {
        activeCenterSection: 'gamedesign',
        notesView: 'dashboard',
        corkboardId: null,
        timelineId: null,
        gameTool: gameDesignMatch[1] || 'erd',
      };
    }

    // Check for corkboards routes first
    // Match: /notes/corkboards/:corkboardId
    const corkboardEditorMatch = path.match(/\/notes\/corkboards\/([^/]+)$/);
    if (corkboardEditorMatch) {
      return {
        activeCenterSection: 'corkboards',
        notesView: 'dashboard',
        corkboardId: corkboardEditorMatch[1],
        timelineId: null,
      };
    }

    // Match: /notes/corkboards (list view)
    const corkboardsListMatch = path.match(/\/notes\/corkboards\/?$/);
    if (corkboardsListMatch) {
      return {
        activeCenterSection: 'corkboards',
        notesView: 'dashboard',
        corkboardId: null,
        timelineId: null,
      };
    }

    // Check for timelines routes
    // Match: /notes/timelines/:timelineId
    const timelineEditorMatch = path.match(/\/notes\/timelines\/([^/]+)$/);
    if (timelineEditorMatch) {
      return {
        activeCenterSection: 'timelines',
        notesView: 'dashboard',
        corkboardId: null,
        timelineId: timelineEditorMatch[1],
      };
    }

    // Match: /notes/timelines (list view)
    const timelinesListMatch = path.match(/\/notes\/timelines\/?$/);
    if (timelinesListMatch) {
      return {
        activeCenterSection: 'timelines',
        notesView: 'dashboard',
        corkboardId: null,
        timelineId: null,
      };
    }

    // Check for characters routes
    // Match: /characters
    const charactersMatch = path.match(/\/characters\/?$/);
    if (charactersMatch) {
      return {
        activeCenterSection: 'characters',
        notesView: 'dashboard',
        corkboardId: null,
        timelineId: null,
      };
    }

    // Match other notes routes
    const notesMatch = path.match(/\/notes(?:\/(.+))?$/);
    if (notesMatch) {
      const subView = notesMatch[1]; // 'list', 'corkboard', 'timeline', or undefined
      return {
        activeCenterSection: 'notes',
        notesView: subView || 'dashboard',
        corkboardId: null,
        timelineId: null,
      };
    }

    return {
      activeCenterSection: 'manuscript',
      notesView: 'dashboard',
      corkboardId: null,
      timelineId: null,
    };
  }, [location.pathname]);

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) {
        navigate(urls.dashboard);
        return;
      }

      try {
        dispatch(setLoading(true));
        const response = await projectService.getProjectById(projectId);

        if (response?.data?.project) {
          dispatch(setActiveProject(response.data.project));
        } else {
          dispatch(setError('Project not found'));
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        dispatch(setError('Failed to load project'));
      } finally {
        dispatch(setLoading(false));
        setLocalLoading(false);
      }
    };

    fetchProject();
  }, [projectId, navigate, dispatch]);

  const handleBackToDashboard = () => {
    navigate(urls.dashboard);
  };

  const handleEditProject = () => {
    setEditProjectModalOpen(true);
  };

  const handleProjectUpdated = (updatedProject) => {
    if (updatedProject && activeProject) {
      dispatch(setActiveProject({ ...activeProject, ...updatedProject }));
    }
  };

  const handleAddCharacter = () => {
    setAddCharacterModalOpen(true);
  };

  const handleCharacterClick = useCallback(() => {
    navigate(`/projectDetails/${projectId}/characters`);
  }, [navigate, projectId]);

  const handleGameToolChange = useCallback(
    (toolId) => {
      navigate(`/projectDetails/${projectId}/gamedesign/${toolId}`);
    },
    [navigate, projectId]
  );

  const handleAddChapter = () => {
    setAddChapterModalOpen(true);
  };

  const handleImportDocument = () => {
    setImportDocumentModalOpen(true);
  };

  const handleImportComplete = async (importData) => {
    // Refetch project to get updated chapters (bypass cache to get fresh data)
    try {
      const response = await projectService.getProjectById(projectId, { useCache: false });
      if (response?.data?.project) {
        dispatch(setActiveProject(response.data.project));
      }
    } catch (err) {
      console.error('Error refreshing project after import:', err);
    }
  };

  const handleChapterCreated = (chapter) => {
    // Update the Redux store with the new chapter
    dispatch(
      addChapterToProject({
        projectId: activeProject._id,
        chapter: {
          _id: chapter._id,
          name: chapter.name,
        },
      })
    );
    setAddChapterModalOpen(false);
  };

  const handleShareProject = useCallback(() => {
    setShareModalOpen(true);
  }, []);

  // Opens the export options modal; the modal handles the download itself.
  const handleExportProject = useCallback(() => {
    if (!projectId) {
      return;
    }
    setExportModalOpen(true);
  }, [projectId]);

  const handleExported = useCallback(
    (format) => {
      const formatLabels = {
        docx: 'Word document',
        markdown: 'Markdown file',
        html: 'web page',
        txt: 'text file',
      };
      setExportToast({
        severity: 'success',
        message: `Exported "${activeProject?.title || 'project'}" as ${
          formatLabels[format] || 'file'
        }.`,
      });
    },
    [activeProject?.title]
  );

  const handleAddNote = () => {
    // Open the right sidebar; its Notes mode contains the note creation form.
    setRightSidebarOpen(true);
  };

  const handleCenterSectionChange = useCallback(
    (section) => {
      if (section === 'notes') {
        navigate(`/projectDetails/${projectId}/notes`);
      } else if (section === 'characters') {
        navigate(`/projectDetails/${projectId}/characters`);
      } else if (section === 'gamedesign') {
        navigate(`/projectDetails/${projectId}/gamedesign`);
      } else if (section === 'manuscript') {
        navigate(`/projectDetails/${projectId}`);
      }
    },
    [navigate, projectId]
  );

  const handleChapterClick = useCallback((chapter, options = {}) => {
    if (!chapter) {
      return;
    }

    const identifier =
      chapter.id || chapter._id || chapter.chapterId || chapter?.chapter?.id || null;

    if (!identifier) {
      console.warn('Unable to determine chapter identifier for scrolling.', chapter);
      return;
    }

    const manualExpand = options?.expandScenes || false;

    if (!manualExpand) {
      setSuppressSceneLoading(true);

      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }
      suppressTimeoutRef.current = setTimeout(() => {
        setSuppressSceneLoading(false);
        suppressTimeoutRef.current = null;
      }, 700);
    }

    const element = document.getElementById(`chapter-${identifier}`);
    if (element) {
      const actionBar = document.getElementById('manuscript-action-bar');
      const actionBarHeight = actionBar ? actionBar.getBoundingClientRect().height : 0;
      const offset = actionBarHeight + 24; // add a bit of breathing room

      const scrollContainer = document.getElementById('manuscript-scroll-container');
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const targetScroll =
          scrollContainer.scrollTop + (elementRect.top - containerRect.top) - offset;

        scrollContainer.scrollTo({
          top: Math.max(targetScroll, 0),
          left: 0,
          behavior: 'auto',
        });
      } else {
        const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
        const targetScroll = elementTop - offset;

        window.scrollTo({
          top: targetScroll,
          left: 0,
          behavior: 'auto',
        });
      }
    }
  }, []);

  const handleAddScene = (chapterId) => {
    const chapters = activeProject.thinChapters || [];
    const chapter = chapters.find((ch) => ch.id === chapterId);
    setSelectedChapterForScene({ id: chapterId, name: chapter?.name || 'Unknown Chapter' });
    setAddSceneModalOpen(true);
  };

  // Callback for chapters to register their addScene functions
  const handleSceneAdded = useCallback((chapterId, addSceneFunction) => {
    console.log('Chapter registering addScene function:', chapterId);
    setChapterSceneFunctions((prev) => ({
      ...prev,
      [chapterId]: addSceneFunction,
    }));
  }, []);

  const handleSceneCreated = (scene) => {
    console.log('Scene created:', scene);
    console.log('Selected chapter:', selectedChapterForScene);

    // Update the Redux store with the new scene
    dispatch(
      addSceneToChapter({
        chapterId: selectedChapterForScene.id,
        scene: {
          _id: scene._id,
          id: scene.id,
          name: scene.name,
          description: scene.description,
          type: scene.type,
          position: scene.position,
        },
      })
    );

    // Add scene to the chapter's local state using the registered function
    const addSceneFn = chapterSceneFunctions[selectedChapterForScene.id];
    if (addSceneFn) {
      console.log('Adding scene to chapter using registered function');
      addSceneFn(scene);
    } else {
      console.warn('No addScene function registered for chapter:', selectedChapterForScene.id);
    }

    setAddSceneModalOpen(false);
    setSelectedChapterForScene(null);

    // Refresh scenes for the chapter if callback is available (legacy)
    if (refreshScenesCallback) {
      refreshScenesCallback(selectedChapterForScene.id);
    }
  };

  const handleSceneClick = useCallback(
    (sceneId, chapterId) => {
      console.log('[handleSceneClick] Called with:', { sceneId, chapterId });

      if (!sceneId || !chapterId) {
        console.warn('[handleSceneClick] Missing sceneId or chapterId');
        return;
      }

      // First, ensure the chapter is visible and scenes are expanded
      const chapters = activeProject?.thinChapters || [];
      const chapter = chapters.find((ch) => ch.id === chapterId || ch._id === chapterId);

      if (!chapter) {
        console.warn('[handleSceneClick] Chapter not found:', chapterId);
        return;
      }

      console.log('[handleSceneClick] Found chapter:', chapter);

      // Helper function to find scene element by trying multiple ID formats
      const findSceneElement = (searchId) => {
        // Try the primary format: scene-${searchId}
        const element = document.getElementById(`scene-${searchId}`);
        if (element) return element;

        // Try to find by data-scene-mongo-id attribute (navigation uses MongoDB _id)
        const elementByMongoId = document.querySelector(`[data-scene-mongo-id="${searchId}"]`);
        if (elementByMongoId) {
          console.log('[handleSceneClick] Found scene by data-scene-mongo-id:', searchId);
          return elementByMongoId;
        }

        // If not found, try to find by searching all scene elements and matching the data
        // This handles cases where the ID format might differ
        const allScenes = document.querySelectorAll('[id^="scene-"]');
        const matchingScene = Array.from(allScenes).find((sceneEl) => {
          // Check if this element's ID contains our search ID (handles both id and _id cases)
          const elId = sceneEl.id.replace('scene-', '');
          return elId === searchId;
        });
        return matchingScene || null;
      };

      const scrollToSceneElement = (element) => {
        console.log('[handleSceneClick] Scrolling to scene element:', {
          elementId: element.id,
          elementTop: element.getBoundingClientRect().top,
        });

        const actionBar = document.getElementById('manuscript-action-bar');
        const actionBarHeight = actionBar ? actionBar.getBoundingClientRect().height : 0;
        const offset = actionBarHeight + 24;

        const scrollContainer = document.getElementById('manuscript-scroll-container');
        if (scrollContainer) {
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            const containerRect = scrollContainer.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const targetScroll =
              scrollContainer.scrollTop + (elementRect.top - containerRect.top) - offset;

            console.log('[handleSceneClick] Executing scroll to scene:', {
              targetScroll,
              currentScroll: scrollContainer.scrollTop,
              elementTop: elementRect.top,
              containerTop: containerRect.top,
            });

            scrollContainer.scrollTo({
              top: Math.max(targetScroll, 0),
              left: 0,
              behavior: 'smooth',
            });
          });
        } else {
          const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
          const targetScroll = elementTop - offset;

          console.log('[handleSceneClick] Using window.scrollTo:', { targetScroll });
          window.scrollTo({
            top: targetScroll,
            left: 0,
            behavior: 'smooth',
          });
        }
      };

      // Check if scene element already exists - if so, scroll directly to it
      const sceneElementId = `scene-${sceneId}`;
      console.log('[handleSceneClick] Looking for scene element with ID:', sceneElementId);

      // Debug: Log all scene elements in the DOM
      const allSceneElements = document.querySelectorAll('[id^="scene-"]');
      const allSceneIds = Array.from(allSceneElements).map((el) => el.id);
      console.log('[handleSceneClick] All scene elements in DOM:', {
        count: allSceneElements.length,
        ids: allSceneIds,
        searchingFor: sceneElementId,
        match: allSceneIds.includes(sceneElementId),
      });

      let sceneElement = findSceneElement(sceneId);

      console.log('[handleSceneClick] Initial scene element check:', {
        found: !!sceneElement,
        elementId: sceneElementId,
        sceneId,
        actualElementId: sceneElement?.id,
      });

      if (sceneElement) {
        // Scene already exists, scroll directly to it (skip chapter scroll)
        console.log('[handleSceneClick] Scene found immediately, scrolling directly to scene');
        scrollToSceneElement(sceneElement);
        return;
      }

      // Scene not found yet - scroll to chapter first to ensure it's visible and scenes load
      const chapterIdentifier = chapter.id || chapter._id || chapter.chapterId || null;
      const chapterElement = chapterIdentifier
        ? document.getElementById(`chapter-${chapterIdentifier}`)
        : null;

      console.log('[handleSceneClick] Chapter element:', {
        chapterIdentifier,
        found: !!chapterElement,
        elementId: chapterElement ? `chapter-${chapterIdentifier}` : null,
      });

      if (chapterElement) {
        const actionBar = document.getElementById('manuscript-action-bar');
        const actionBarHeight = actionBar ? actionBar.getBoundingClientRect().height : 0;
        const offset = actionBarHeight + 24;

        const scrollContainer = document.getElementById('manuscript-scroll-container');
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = chapterElement.getBoundingClientRect();
          const targetScroll =
            scrollContainer.scrollTop + (elementRect.top - containerRect.top) - offset;

          console.log('[handleSceneClick] Scrolling to chapter:', {
            targetScroll,
            currentScroll: scrollContainer.scrollTop,
          });

          scrollContainer.scrollTo({
            top: Math.max(targetScroll, 0),
            left: 0,
            behavior: 'auto',
          });
        }
      }

      // Scene not found yet, set up polling and MutationObserver
      let attempts = 0;
      const maxAttempts = 50; // Try for up to 5 seconds (50 * 100ms)

      // Use MutationObserver to watch for when the scene element is added to the DOM
      const observer = new MutationObserver(() => {
        sceneElement = findSceneElement(sceneId);
        if (sceneElement) {
          console.log('[handleSceneClick] MutationObserver found scene element:', sceneElement.id);
          observer.disconnect();
          scrollToSceneElement(sceneElement);
        }
      });

      // Start observing the chapter element or manuscript container for changes
      const observeTarget =
        chapterElement || document.getElementById('manuscript-scroll-container') || document.body;
      console.log('[handleSceneClick] Setting up MutationObserver on:', {
        target: observeTarget?.id || observeTarget?.tagName,
      });
      observer.observe(observeTarget, {
        childList: true,
        subtree: true,
      });

      // Also poll as a fallback
      const pollForScene = () => {
        attempts += 1;
        sceneElement = findSceneElement(sceneId);

        if (sceneElement) {
          console.log(
            '[handleSceneClick] Polling found scene element on attempt:',
            attempts,
            sceneElement.id
          );
          observer.disconnect();
          scrollToSceneElement(sceneElement);
        } else if (attempts >= maxAttempts) {
          observer.disconnect();
          // Log all scene elements again for debugging
          const allScenes = document.querySelectorAll('[id^="scene-"]');
          console.error(
            `[handleSceneClick] Scene element with sceneId "${sceneId}" not found after ${maxAttempts} attempts`,
            {
              searchedId: sceneId,
              searchedElementId: sceneElementId,
              availableIds: Array.from(allScenes).map((el) => el.id),
            }
          );
        } else {
          if (attempts % 10 === 0) {
            // Log every 10th attempt - also log current scene IDs
            const currentScenes = document.querySelectorAll('[id^="scene-"]');
            console.log(`[handleSceneClick] Polling attempt ${attempts}/${maxAttempts}`, {
              currentSceneIds: Array.from(currentScenes).map((el) => el.id),
            });
          }
          setTimeout(pollForScene, 100);
        }
      };

      // Start polling after a brief delay to allow chapter to render
      console.log('[handleSceneClick] Starting polling in 300ms');
      setTimeout(pollForScene, 300);
    },
    [activeProject]
  );

  const toggleLeftSidebar = () => {
    setLeftSidebarOpen(!leftSidebarOpen);
  };

  const toggleRightSidebar = () => {
    setRightSidebarOpen(!rightSidebarOpen);
  };

  const handleLeftSidebarResize = (newWidth) => {
    setLeftSidebarWidth(Math.max(200, Math.min(500, newWidth)));
  };

  const handleRightSidebarResize = (newWidth) => {
    setRightSidebarWidth(Math.max(200, Math.min(500, newWidth)));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  useEffect(() => {
    return () => {
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }
    };
  }, []);

  // Command palette: chapters, scenes, characters, notes, navigation, actions
  useProjectCommands({
    activeProject,
    projectId,
    activeCenterSection,
    handleChapterClick,
    handleSceneClick,
    handleAddChapter,
    handleAddCharacter,
    handleAddNote,
    handleImportDocument,
    handleExportProject,
    handleEditProject,
    handleShareProject,
  });

  // Only show loading screen if we don't already have the project data
  // This prevents the loading screen from showing when navigating between sections (notes/manuscript)
  const isProjectLoaded = activeProject && activeProject._id === projectId;

  if ((localLoading || loading) && !isProjectLoaded) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        overflow="hidden"
        sx={{
          backgroundColor: colors.background.primary,
        }}
      >
        <CircularProgress sx={{ color: colors.interactive.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        p={4}
        height="100%"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        sx={{
          backgroundColor: colors.background.primary,
        }}
      >
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: colors.semantic.error,
            color: colors.text.primary,
            '& .MuiAlert-icon': {
              color: colors.text.primary,
            },
          }}
        >
          {error}
        </Alert>
        <Button
          onClick={handleBackToDashboard}
          startIcon={<ArrowBack sx={{ color: colors.icon.primary }} />}
          sx={{
            backgroundColor: colors.interactive.primary,
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: colors.interactive.hover,
            },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (!activeProject) {
    return (
      <Box
        p={4}
        height="100%"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        sx={{
          backgroundColor: colors.background.primary,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: colors.text.primary,
            mb: 2,
          }}
        >
          Project not found
        </Typography>
        <Button
          onClick={handleBackToDashboard}
          startIcon={<ArrowBack sx={{ color: colors.icon.primary }} />}
          sx={{
            backgroundColor: colors.interactive.primary,
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: colors.interactive.hover,
            },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const chapters = activeProject.thinChapters || [];
  const notes = activeProject.thinNotes || [];
  const collaborators = [
    ...(activeProject.thinCoauthors || []),
    ...(activeProject.thinEditors || []),
  ];
  const characters =
    activeProject.thinCharacters || activeProject.characters || activeProject.characterList || [];

  return (
    <ProjectAccessProvider role={activeProject.myRole || 'owner'}>
      <ProjectLayout
        leftSidebarOpen={leftSidebarOpen}
        rightSidebarOpen={rightSidebarOpen}
        toggleLeftSidebar={toggleLeftSidebar}
        toggleRightSidebar={toggleRightSidebar}
        leftSidebarWidth={leftSidebarWidth}
        rightSidebarWidth={rightSidebarWidth}
        handleLeftSidebarResize={handleLeftSidebarResize}
        handleRightSidebarResize={handleRightSidebarResize}
        activeProject={activeProject}
        chapters={chapters}
        notes={notes}
        collaborators={collaborators}
        characters={characters}
        handleBackToDashboard={handleBackToDashboard}
        handleEditProject={handleEditProject}
        handleAddChapter={handleAddChapter}
        handleAddNote={handleAddNote}
        handleAddCharacter={handleAddCharacter}
        onCharacterClick={handleCharacterClick}
        handleChapterClick={handleChapterClick}
        handleAddScene={handleAddScene}
        handleSceneClick={handleSceneClick}
        setRefreshScenesCallback={setRefreshScenesCallback}
        onSceneAdded={handleSceneAdded}
        formatDate={formatDate}
        suppressSceneLoading={suppressSceneLoading}
        handleExportProject={handleExportProject}
        handleImportDocument={handleImportDocument}
        handleShareProject={handleShareProject}
        activeCenterSection={activeCenterSection}
        notesView={notesView}
        corkboardId={corkboardId}
        timelineId={timelineId}
        gameTool={gameTool || 'erd'}
        onGameToolChange={handleGameToolChange}
        onCenterSectionChange={handleCenterSectionChange}
      />
      <AddChapterModal
        open={addChapterModalOpen}
        onClose={() => setAddChapterModalOpen(false)}
        onChapterCreated={handleChapterCreated}
        projectId={projectId}
        projectType={activeProject?.type}
      />
      <AddSceneModal
        open={addSceneModalOpen}
        onClose={() => {
          setAddSceneModalOpen(false);
          setSelectedChapterForScene(null);
        }}
        onSceneCreated={handleSceneCreated}
        chapterId={selectedChapterForScene?.id || ''}
        chapterName={selectedChapterForScene?.name || ''}
        projectType={activeProject?.type}
      />
      <ImportDocumentModal
        open={importDocumentModalOpen}
        onClose={() => setImportDocumentModalOpen(false)}
        projectId={projectId}
        onImportComplete={handleImportComplete}
      />
      <ExportProjectModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        projectId={projectId}
        projectTitle={activeProject?.title || ''}
        onExported={handleExported}
      />
      <EditProjectModal
        open={editProjectModalOpen}
        onClose={() => setEditProjectModalOpen(false)}
        project={activeProject}
        onProjectUpdated={handleProjectUpdated}
      />
      <AddCharacterModal
        open={addCharacterModalOpen}
        onClose={() => setAddCharacterModalOpen(false)}
        projectId={projectId}
      />
      <ShareProjectModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        projectId={projectId}
        projectTitle={activeProject?.title || ''}
        onLeftProject={() => navigate(urls.dashboard)}
      />
      <Snackbar
        open={Boolean(exportToast)}
        autoHideDuration={4000}
        onClose={() => setExportToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={exportToast?.severity || 'info'}
          variant="filled"
          onClose={() => setExportToast(null)}
        >
          {exportToast?.message || ''}
        </Alert>
      </Snackbar>
    </ProjectAccessProvider>
  );
};

export default ProjectView;
